import { useMatterConflictConfirmation } from "@/components/features/matter/layout/useMatterConflictConfirmation";
import { getIncidentTypeLabel } from "@/lib/utils/matters/incident-classification";
import { retrieveClaimTypesQueryOptions } from "@/lib/data/react-query/matters/claim-type";
import { AttorneyFeeSelect } from "@/components/features/matter/AttorneyFeeSelect/AttorneyFeeSelect";
import {
  MultiselectFormField,
  CountrySelectFormField,
  DateFormField,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormSubmitButton,
  PlacesAutocompleteFormField,
  SelectFormField,
  StateSelectFormField,
  TextAreaFormField,
  TextFormField,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { AddContactFormField } from "@/components/ui/FormFields/AddContactFormField/AddContactFormField";
import { CheckboxFormField } from "@/components/ui/FormFields/CheckboxFormField/CheckboxFormField";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { MultiCategorySelectFormField } from "@/components/ui/FormFields/MultiCategorySelectFormField/MultiCategorySelectFormField";
import { TimeFormField } from "@/components/ui/FormFields/TimeFormField/TimeFormField";
import {
  useCreateMatter,
  useUpdateMatter,
} from "@/lib/data/react-query/matters/matter";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { useCreateMatterStatusPhase } from "@/lib/data/react-query/matters/matter-status";
import { getMatterContactConnections } from "@/lib/utils/matters/matter-contact-connection-party-roles";
import { createContactConnectionChildInput } from "@/lib/utils/connections/item-contact-connections";
import { MATTER_PARTY_CONNECTION_TYPES } from "@/lib/utils/connections/matter-party-connections";
import { ContactConnectionInFormSchema } from "@/lib/data/schemas";
import { MATTER_DEFAULT_ATTORNEY_FEE_OPTIONS } from "@/lib/data/static/matterStatics";
import {
  type APIResponse,
  type Contact,
  type Matter,
  type MatterStatus,
} from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { sortBy } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

type ConditionalProps =
  | {
      editing: true;
      activeMatter: Matter;
    }
  | {
      editing: false;
      activeMatter?: never;
    };

const formSchema = z
  .object({
    attorneyFee: z.coerce.number({
      required_error: "Attorney fee is required",
    }),
    clientNoSocialSecurity: z.boolean().optional(),
    criticalInfo: z.string().optional().or(z.literal("")),
    dateOfLoss: z.string().optional(),
    incidentAddressCity: z.string().optional().or(z.literal("")),
    incidentAddressCountry: z
      .string()
      .min(1, { message: "Country is required" }),
    incidentAddressCounty: z.string().optional().or(z.literal("")),
    incidentAddressDetails: z.string().optional().or(z.literal("")),
    incidentAddressState: z.string().min(1, { message: "State is required" }),
    incidentAddressStreet1: z.string().optional().or(z.literal("")),
    incidentAddressStreet2: z.string().optional().or(z.literal("")),
    incidentAddressZip: z.string().optional().or(z.literal("")),
    incidentDescription: z.string().optional().or(z.literal("")),
    incidentTime: z.string().optional(),
    statusId: z.string().min(1, { message: "Status is required" }),
    teamId: z.string().optional().or(z.literal("")),
    title: z.string().min(1, { message: "Title is required" }),
    claimTypeIds: z.array(z.string()),
    incidentTypeId: z.string().min(1, { message: "Type is required" }),
  })
  .merge(
    z.object({
      clientConnection: ContactConnectionInFormSchema.refine((val) => val?.id, {
        message: "Client is required",
      }),
      primaryContactConnection: ContactConnectionInFormSchema.refine(
        (val) => val?.id,
        {
          message: "Primary contact is required",
        },
      ),
    }),
  );
const defaultFormValues = {
  attorneyFee: 33.33,
  dateOfLoss: "",
  incidentTime: "",
  incidentDescription: "",
  title: "",
  clientConnection: {
    id: "",
    type: "individual",
    individualFirstName: "",
    individualLastName: "",
    organizationName: "",
  },
  primaryContactConnection: {
    id: "",
    type: "individual",
    individualFirstName: "",
    individualLastName: "",
    organizationName: "",
  },
  statusId: "",
  incidentTypeId: "",
  claimTypeIds: [] as string[],
  teamId: "",
  incidentAddressStreet1: "",
  incidentAddressStreet2: "",
  incidentAddressDetails: "",
  incidentAddressCity: "",
  incidentAddressCounty: "",
  incidentAddressState: "",
  incidentAddressZip: "",
  incidentAddressCountry: "United States",
  criticalInfo: "",
  clientNoSocialSecurity: false,
} as const;

type AddOrEditMatterFormValues = z.infer<typeof formSchema>;

interface AddOrEditMatterModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  creatingMore?: boolean;
  afterCreateFn?: (res: APIResponse<Matter>) => void | Promise<void>;
  initialFormValues?: Partial<AddOrEditMatterFormValues>;
  allowedMatterStatusCategories?: MatterStatus["category"]["id"][];
  /** Centered dialog instead of a side slideover (e.g. matters list create). */
  massTortId?: string;
}

export const AddOrEditMatterModal = ({
  open,
  setOpen,
  editing,
  activeMatter: activeMatter,
  afterCreateFn,
  initialFormValues,
  allowedMatterStatusCategories,
  massTortId,
}: AddOrEditMatterModalProps & ConditionalProps) => {
  const MATTER_FORM_ID = "add-or-edit-matter-form";
  const formRef = useRef<HTMLFormElement>(null);
  const creationInitialValues = useMemo(
    () => ({
      ...defaultFormValues,
      ...(initialFormValues ?? {}),
    }),
    [initialFormValues],
  );
  const form = useForm<AddOrEditMatterFormValues>({
    resolver: zodResolver(formSchema),
    values: editing
      ? {
          ...defaultFormValues,
          attorneyFee: Number(
            activeMatter.attorneyFee ?? defaultFormValues.attorneyFee,
          ),
          clientConnection:
            getMatterContactConnections(activeMatter).find(
              (connection) => connection.label === "Client",
            )?.contact ?? defaultFormValues.clientConnection,
          primaryContactConnection:
            getMatterContactConnections(activeMatter).find(
              (connection) => connection.label === "Primary Contact",
            )?.contact ?? defaultFormValues.primaryContactConnection,
          criticalInfo: activeMatter.criticalInfo ?? "",
          dateOfLoss: activeMatter.dateOfLoss ?? "",
          incidentAddressCity: activeMatter.incidentAddressCity ?? "",
          incidentAddressCountry: activeMatter.incidentAddressCountry ?? "",
          incidentAddressCounty: activeMatter.incidentAddressCounty ?? "",
          incidentAddressDetails: activeMatter.incidentAddressDetails ?? "",
          incidentAddressState: activeMatter.incidentAddressState ?? "",
          incidentAddressStreet1: activeMatter.incidentAddressStreet1 ?? "",
          incidentAddressStreet2: activeMatter.incidentAddressStreet2 ?? "",
          incidentAddressZip: activeMatter.incidentAddressZip ?? "",
          incidentDescription: activeMatter.incidentDescription ?? "",
          incidentTime: activeMatter.incidentTime ?? "",
          statusId: activeMatter.statusId,
          teamId: "",
          title: activeMatter.title,
          incidentTypeId: activeMatter.incidentTypeId ?? "",
          claimTypeIds: activeMatter.claimTypeIds,
        }
      : defaultFormValues,
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });
  const { confirmParty, conflictModal, isCheckingConflict } =
    useMatterConflictConfirmation(open);
  const updateMatter = useUpdateMatter();
  const createMatter = useCreateMatter();
  const createMatterPhase = useCreateMatterStatusPhase();
  const [submitLifecycleLoading, setSubmitLifecycleLoading] = useState(false);
  const isFormLoading =
    isCheckingConflict ||
    updateMatter.isPending ||
    createMatter.isPending ||
    createMatterPhase.isPending ||
    submitLifecycleLoading;

  useEffect(() => {
    if (editing || !initialFormValues) {
      return;
    }
    form.reset(creationInitialValues);
  }, [creationInitialValues, editing, form, initialFormValues]);

  const {
    data: matterSettingsData,
    isLoading: isMatterSettingsLoading,
    isError: isMatterSettingsError,
  } = useQuery(retrieveMatterSettingsQueryOptions());

  const filteredMatterStatuses = useMemo(() => {
    const statuses = matterSettingsData?.data.statuses ?? [];
    if (!allowedMatterStatusCategories?.length) {
      return statuses;
    }
    return statuses.filter((status) =>
      allowedMatterStatusCategories.includes(status.categoryId),
    );
  }, [allowedMatterStatusCategories, matterSettingsData?.data.statuses]);

  const matterStatuses = sortBy(filteredMatterStatuses, (status) => {
    return status.category.label;
  }).reduce<{ category: string; items: { value: string; label: string }[] }[]>(
    (acc, status) => {
      const category = status.categoryId;
      const item = {
        value: status.id,
        label: status.label,
      };
      const existingCategory = acc.find((c) => c.category === category);
      if (existingCategory) {
        existingCategory.items.push(item);
      }
      if (!existingCategory) {
        acc.push({ category, items: [item] });
      }

      return acc;
    },
    [],
  );

  const {
    data: claimTypeData,
    isLoading: isClaimTypesLoading,
    isError: isClaimTypesError,
  } = useQuery(retrieveClaimTypesQueryOptions());
  const incidentTypes = matterSettingsData?.data.incidentTypes ?? [];
  const teams = matterSettingsData?.data.teams ?? [];
  const [matterStatusOpen, setMatterStatusOpen] = useState(false);
  const [creatingMore] = useState(false);

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    if (
      !editing &&
      !(await confirmParty({
        contactId: data.clientConnection?.id ?? "",
        label: "Client",
      }))
    ) {
      return;
    }
    const { teamId, statusId } = data;
    const matterFormData = {
      attorneyFee: data.attorneyFee,
      clientNoSocialSecurity: data.clientNoSocialSecurity,
      criticalInfo: data.criticalInfo,
      dateOfLoss: data.dateOfLoss,
      incidentAddressCity: data.incidentAddressCity,
      incidentAddressCountry: data.incidentAddressCountry,
      incidentAddressCounty: data.incidentAddressCounty,
      incidentAddressDetails: data.incidentAddressDetails,
      incidentAddressState: data.incidentAddressState,
      incidentAddressStreet1: data.incidentAddressStreet1,
      incidentAddressStreet2: data.incidentAddressStreet2,
      incidentAddressZip: data.incidentAddressZip,
      incidentDescription: data.incidentDescription,
      incidentTime: data.incidentTime,
      title: data.title,
      incidentTypeId: data.incidentTypeId,
      claimTypeIds: data.claimTypeIds,
    };
    if (editing) {
      await updateMatter.mutateAsync({
        dateModified: activeMatter.dateModified,
        id: activeMatter.id,
        ...matterFormData,
      });
      if (statusId && statusId !== activeMatter.statusId) {
        await createMatterPhase.mutateAsync({
          dateTimeStart: new Date().toISOString(),
          matterId: activeMatter.id,
          statusId,
        });
      }
      setOpen(false);
    } else {
      setSubmitLifecycleLoading(true);
      try {
        const result = await createMatter.mutateAsync({
          attorneyFee: matterFormData.attorneyFee,
          clientNoSocialSecurity: matterFormData.clientNoSocialSecurity,
          connections: [
            createContactConnectionChildInput(
              data.clientConnection?.id ?? "",
              MATTER_PARTY_CONNECTION_TYPES.Client,
            ),
            createContactConnectionChildInput(
              data.primaryContactConnection?.id ?? "",
              MATTER_PARTY_CONNECTION_TYPES["Primary Contact"],
            ),
          ],
          criticalInfo: matterFormData.criticalInfo,
          dateOfLoss: matterFormData.dateOfLoss || undefined,
          incidentAddressCity: matterFormData.incidentAddressCity,
          incidentAddressCountry: matterFormData.incidentAddressCountry,
          incidentAddressCounty: matterFormData.incidentAddressCounty,
          incidentAddressDetails: matterFormData.incidentAddressDetails,
          incidentAddressState: matterFormData.incidentAddressState,
          incidentAddressStreet1: matterFormData.incidentAddressStreet1,
          incidentAddressStreet2: matterFormData.incidentAddressStreet2,
          incidentAddressZip: matterFormData.incidentAddressZip,
          incidentDescription: matterFormData.incidentDescription,
          incidentTime: matterFormData.incidentTime,
          ...(massTortId ? { massTortId } : {}),
          statusId,
          teamId: teamId || undefined,
          title: matterFormData.title,
          incidentTypeId: matterFormData.incidentTypeId,
          claimTypeIds: matterFormData.claimTypeIds,
        });
        if (creatingMore) {
          setOpen(false);
          form.reset();
          return;
        }
        await Promise.resolve(afterCreateFn?.(result));
        setOpen(false);
      } finally {
        setSubmitLifecycleLoading(false);
      }
    }
  };

  const scrollToFirstInvalidField = () => {
    const formElement = formRef.current;
    if (!formElement) {
      return;
    }

    requestAnimationFrame(() => {
      const firstInvalidField = formElement.querySelector<HTMLElement>(
        '[aria-invalid="true"]',
      );
      if (!firstInvalidField) {
        return;
      }

      firstInvalidField.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });

      const focusTarget = firstInvalidField.matches(
        'input, textarea, select, button, [role="combobox"]',
      )
        ? firstInvalidField
        : firstInvalidField.querySelector<HTMLElement>(
            'input, textarea, select, button, [role="combobox"]',
          );

      focusTarget?.focus({ preventScroll: true });
    });
  };

  const dateOfLossFormValue = form.watch("dateOfLoss");
  const clientConnectionFormValue = form.watch("clientConnection");

  useEffect(() => {
    const titleFormValue = form.getFieldState("title");
    if (titleFormValue.isDirty) {
      return;
    }
    let dateOfLoss = "";
    let clientName = "";
    if (dateOfLossFormValue) {
      dateOfLoss = dayjs(dateOfLossFormValue).format("MM/DD/YY");
    }
    const { individualFirstName, individualLastName, organizationName } =
      clientConnectionFormValue as Partial<Contact>;
    if (individualLastName && individualFirstName) {
      clientName = `${individualLastName}, ${individualFirstName}`;
    } else if (individualLastName) {
      clientName = individualLastName;
    } else if (individualFirstName) {
      clientName = individualFirstName;
    } else if (organizationName) {
      clientName = organizationName;
    }

    form.setValue(
      "title",
      `${clientName}${dateOfLoss ? ` - DOL ${dateOfLoss}` : ""}`,
    );
  }, [dateOfLossFormValue, clientConnectionFormValue, form]);

  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [open, form]);

  const titleText = editing ? "Update matter details" : "Create matter";

  const matterFieldset = (
    <fieldset className={cn(slideoverFieldsetClassName, "gap-y-3")}>
      <AddContactFormField
        matterId={editing ? activeMatter.id : undefined}
        register={form.register("clientConnection")}
        label={"Client*"}
        contactPreviewTone="client"
        onDelete={() => {
          form.setValue("clientConnection", defaultFormValues.clientConnection);
        }}
        defaultValues={{
          type: "individual",
          individualSubTypes: ["Client"],
        }}
      />
      <AddContactFormField
        matterId={editing ? activeMatter.id : undefined}
        register={form.register("primaryContactConnection")}
        label={"Primary contact*"}
        contactPreviewTone="clientGray"
        onDelete={() => {
          form.setValue(
            "primaryContactConnection",
            defaultFormValues.primaryContactConnection,
          );
        }}
        showSelectClientButton
        client={
          clientConnectionFormValue?.id
            ? (clientConnectionFormValue as unknown as Contact)
            : undefined
        }
      />
      <div>
        <CheckboxFormField
          register={form.register("clientNoSocialSecurity")}
          label="No client SSN"
        />
      </div>
      <div className="hidden sm:block" aria-hidden="true" />
      <TextFormField
        register={form.register("title")}
        label="Title*"
        dataTestId="matter-title"
      />
      <MultiCategorySelectFormField
        register={form.register("statusId")}
        label="Status*"
        items={matterStatuses}
        placeholder="Select a status"
        searchPlaceholder="Search statuses..."
        emptyMessage="No status found."
        open={matterStatusOpen}
        setOpen={setMatterStatusOpen}
        isItemsError={isMatterSettingsError}
        isItemsLoading={isMatterSettingsLoading}
      />
      <SelectFormField<{ id: string; label: string }>
        isItemsLoading={isMatterSettingsLoading}
        isItemsError={isMatterSettingsError}
        label="Incident type*"
        placeholder="Select an incident type"
        register={form.register("incidentTypeId")}
        items={incidentTypes.map((type) => ({
          ...type,
          label: getIncidentTypeLabel(type),
        }))}
        itemsKey="id"
        formatItemLabel={(item) => item?.label}
      />
      <MultiselectFormField
        label="Claim types"
        register={form.register("claimTypeIds")}
        options={(claimTypeData?.data ?? []).map((claimType) => ({
          value: claimType.id,
          label: claimType.label,
        }))}
        disabled={isClaimTypesLoading || isClaimTypesError}
      />
      {isClaimTypesError && (
        <p role="alert" className="text-destructive text-sm">
          Claim types could not be loaded. Try again before changing claims.
        </p>
      )}
      <SelectFormField<{ id: string; label: string }>
        isItemsLoading={isMatterSettingsLoading}
        isItemsError={isMatterSettingsError}
        label="Team"
        placeholder="Select a team"
        register={form.register("teamId")}
        items={teams}
        itemsKey="id"
        formatItemLabel={(item) => item?.label}
      />
      <DateFormField
        register={form.register("dateOfLoss")}
        label="Date of loss"
        name="dateOfLoss"
      />
      <TimeFormField
        register={form.register("incidentTime")}
        label="Time of incident"
        name="incidentTime"
      />
      <FormField
        control={form.control}
        name="attorneyFee"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>Attorney fee* (%)</FormLabel>
              <FormMessage />
            </div>
            <AttorneyFeeSelect
              placeholder={`Select or enter an attorney fee`}
              value={field.value.toString()}
              onChange={(value) => {
                form.setValue("attorneyFee", Number(value));
              }}
              options={MATTER_DEFAULT_ATTORNEY_FEE_OPTIONS}
              createMessage={`Add an attorney fee`}
              emptyMessage={`No attorney fee found`}
              label={``}
            />
          </FormItem>
        )}
      />
      <TextAreaFormField
        register={form.register("criticalInfo")}
        label="Critical matter information"
        className="col-span-full"
        textAreaClassName="field-sizing-content max-h-[20lh]"
      />
      <PlacesAutocompleteFormField
        register={form.register("incidentAddressStreet1")}
        defaultValue={form.getValues("incidentAddressStreet1")}
        label="Incident street address 1"
        onAddressSelect={(address) => {
          form.setValue("incidentAddressCity", address.city, {
            shouldValidate: true,
            shouldDirty: true,
          });
          form.setValue("incidentAddressCounty", address.county, {
            shouldValidate: true,
          });
          form.setValue("incidentAddressState", address.state, {
            shouldValidate: true,
          });
          form.setValue("incidentAddressZip", address.zipCode, {
            shouldValidate: true,
          });
          form.setValue("incidentAddressStreet1", address.streetAddress, {
            shouldValidate: true,
          });
          form.setValue("incidentAddressCountry", address.country, {
            shouldValidate: true,
          });
          form.setValue("incidentAddressStreet2", "");
        }}
      />
      <TextFormField
        register={form.register("incidentAddressStreet2")}
        label="Incident street address 2"
      />
      <TextFormField
        register={form.register("incidentAddressZip")}
        label="Incident zip code"
      />
      <TextFormField
        register={form.register("incidentAddressCity")}
        label="Incident city"
      />
      <TextFormField
        register={form.register("incidentAddressCounty")}
        label="Incident county"
      />
      <StateSelectFormField
        register={form.register("incidentAddressState")}
        label="Incident state*"
        name="incidentAddressState"
        country={form.watch("incidentAddressCountry")}
      />
      <CountrySelectFormField
        register={form.register("incidentAddressCountry")}
        label="Incident country*"
        name="incidentAddressCountry"
        stateSelectName="incidentAddressState"
      />{" "}
      <TextAreaFormField
        register={form.register("incidentAddressDetails")}
        label="Incident location details"
        className="col-span-full"
        textAreaClassName="field-sizing-content max-h-[20lh]"
      />
      <div className="col-span-full">
        <TextAreaFormField
          register={form.register("incidentDescription")}
          label="Incident description"
          className="col-span-2"
          textAreaClassName="field-sizing-content max-h-[20lh]"
        />
      </div>
    </fieldset>
  );

  return (
    <>
      {conflictModal}
      <Form {...form}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={titleText}
          isDirty={form.formState.isDirty}
          loading={isFormLoading}
          size="2xl"
          className="w-[calc(100vw-2rem)] max-w-4xl"
          footer={
            <div className="flex w-full flex-wrap justify-end gap-2">
              <FormCancelButton
                setOpen={setOpen}
                isDirty={form.formState.isDirty}
              />
              <FormSubmitButton
                loading={isFormLoading}
                submitFormId={MATTER_FORM_ID}
              />
            </div>
          }
        >
          <form
            ref={formRef}
            id={MATTER_FORM_ID}
            className="relative"
            onSubmit={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              await form.handleSubmit(onSubmit, scrollToFirstInvalidField)();
            }}
          >
            {isFormLoading && <FormLoadingOverlay />}
            {matterFieldset}
          </form>
        </AppModal>
      </Form>
    </>
  );
};
